/**
 * Escort / extraction — the person you came to get out.
 *
 * An escortee is the inverse of every other NPC in the building: not a threat
 * to avoid but a liability to move. They can't fight, they don't know the
 * building, and they are exactly as visible as you let them be — which turns
 * the whole floor into a routing problem instead of a stealth one.
 *
 * Structurally a `Guard` (movement, pathfinding, the two-tile render model) in
 * its own array, like the janitor and the civilians, so no guard behaviour pass
 * touches them. Three states, and no more: WAITING where you found them,
 * FOLLOWING once told to, DOWNED if they're killed — which fails the contract,
 * because there's nobody left to extract.
 *
 * They mirror your stance: crouch and they crouch. A companion who stands up in
 * the open while you're sneaking would make the mechanic unplayable, and "do
 * what I do" is a rule the player can actually reason about.
 *
 * Pure + headless (rule 4).
 */

import { CONFIG } from '../config';
import { guardSpeed, moveTowards, spawnGuard, type Guard } from './guard';
import { syncVisual } from './movement';
import type { MapData } from './map';
import type { Player } from './player';
import type { World } from './world';

export type EscortState = 'waiting' | 'following' | 'downed';

export interface Escortee extends Guard {
  escortState: EscortState;
  /** Which floor they're standing on — they take the stairs with you. */
  floorIdx: number;
}

const ESCORT_FG = 0xc8a0ff; // violet — reads as neither guard nor civilian

/** Spawn the extraction target where the contract says they are. */
export function spawnEscortee(world: World, x: number, y: number, floorIdx: number): Escortee {
  const e = spawnGuard(world, x, y) as Escortee;
  e.glyph = 'e';
  e.fg = ESCORT_FG;
  e.lastSeenGlyph = 'e';
  e.lastSeenFg = ESCORT_FG;
  e.isJanitor = true; // reuses the non-combatant exclusions (patrol, loot)
  e.escortState = 'waiting';
  e.floorIdx = floorIdx;
  return e;
}

/** Is the player close enough to talk to them? */
export function escortAtHand(e: Escortee, player: Player, floorIdx: number): boolean {
  if (e.escortState === 'downed' || e.floorIdx !== floorIdx) return false;
  return Math.max(Math.abs(e.x - player.x), Math.abs(e.y - player.y)) <= 1;
}

/** Tell them to come with you (or to wait here). Returns the new state. */
export function toggleFollow(e: Escortee): EscortState {
  if (e.escortState === 'downed') return 'downed';
  e.escortState = e.escortState === 'following' ? 'waiting' : 'following';
  e.path = null;
  return e.escortState;
}

/** Are they with the player right now — the thing exfil actually requires? */
export function escortSecured(e: Escortee, player: Player, floorIdx: number): boolean {
  return e.escortState === 'following'
    && e.floorIdx === floorIdx
    && Math.max(Math.abs(e.x - player.x), Math.abs(e.y - player.y)) <= CONFIG.escort.exfilRadius;
}

/** Bring a following escortee along a floor change — they walk the stairs too. */
export function followThroughStairs(e: Escortee, floorIdx: number, arrive: { x: number; y: number }): void {
  if (e.escortState !== 'following' || e.body.dead) return;
  e.floorIdx = floorIdx;
  e.x = arrive.x;
  e.y = arrive.y;
  e.moveDx = 0;
  e.moveDy = 0;
  e.moveProgress = 0;
  e.path = null;
  syncVisual(e);
}

export type EscortEvent = { kind: 'downed' } | null;

/**
 * Advance the escortee one tick. Following keeps them inside a distance band
 * rather than glued to the player: close enough to be extracted, far enough not
 * to shove you in a doorway. Anything outside their own floor is somebody else's
 * problem — they simply stand still.
 */
export function updateEscort(
  e: Escortee,
  map: MapData,
  player: Player,
  floorIdx: number,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
): EscortEvent {
  if (e.body.dead) {
    const first = e.escortState !== 'downed';
    e.escortState = 'downed';
    e.fovTiles = null;
    syncVisual(e);
    return first ? { kind: 'downed' } : null;
  }
  if (e.floorIdx !== floorIdx) { syncVisual(e); return null; }

  // Finish an in-progress step (the step-locked model every NPC shares).
  if (e.moveProgress > 0) {
    const diag = e.moveDx !== 0 && e.moveDy !== 0;
    const rate = guardSpeed(e) * (diag ? 1 / Math.SQRT2 : 1);
    e.moveProgress += rate * tickDt;
    if (e.moveProgress >= 1) {
      e.x += e.moveDx;
      e.y += e.moveDy;
      e.moveProgress = 0;
      e.moveDx = 0;
      e.moveDy = 0;
      if (e.path && e.path.idx < e.path.steps.length - 1) e.path.idx++;
    }
    syncVisual(e);
    return null;
  }

  if (e.escortState !== 'following') { syncVisual(e); return null; }

  // Do what I do: a companion standing tall while you creep would make this
  // unplayable, and it's a rule the player can reason about.
  e.stance = player.stance === 'run' ? 'walk' : player.stance;

  const gap = Math.max(Math.abs(e.x - player.x), Math.abs(e.y - player.y));
  if (gap > CONFIG.escort.followGap) {
    moveTowards(e, player.x, player.y, map, occupied, tickDt, { adjacent: true });
  } else {
    // In position — turn to face them, so the pair reads as a pair.
    e.facingDx = Math.sign(player.x - e.x);
    e.facingDy = Math.sign(player.y - e.y);
  }
  syncVisual(e);
  return null;
}
